import { useState, useEffect, useRef, useCallback } from 'react';
import { getSignedUrl } from '@/api/pets';

// Global cache for signed URLs to prevent refetching across components and renders
const globalPhotoCache: Record<string, string> = {};

interface UsePetPhotoProps {
  petId: string;
  photoKey?: string;
  imageUrl?: string;
}

export function usePetPhoto({ petId, photoKey, imageUrl }: UsePetPhotoProps) {
  // Use cached URL if available as initial state to avoid flicker
  const cacheKey = photoKey || imageUrl || petId;
  const [signedImageUrl, setSignedImageUrl] = useState<string | null>(
    globalPhotoCache[cacheKey] || null
  );

  const lastFetchedPhotoRef = useRef<{
    id: string;
    photoKey?: string;
    imageUrl?: string;
  }>({});
  const isFetchingRef = useRef(false);

  const fetchSignedPhoto = useCallback(
    async (id: string, key?: string, url?: string) => {
      const currentCacheKey = key || url || id;

      if (key) {
        try {
          const { url: signedUrl } = await getSignedUrl(key, 'inline');
          globalPhotoCache[currentCacheKey] = signedUrl;
          setSignedImageUrl(signedUrl);
          lastFetchedPhotoRef.current = { id, photoKey: key, imageUrl: url };
          return signedUrl;
        } catch (error) {
          console.error('Failed to fetch signed photo URL:', error);
          if (url) {
            globalPhotoCache[currentCacheKey] = url;
            setSignedImageUrl(url);
            lastFetchedPhotoRef.current = { id, photoKey: key, imageUrl: url };
            return url;
          }
        }
      } else if (url) {
        globalPhotoCache[currentCacheKey] = url;
        setSignedImageUrl(url);
        lastFetchedPhotoRef.current = { id, photoKey: key, imageUrl: url };
        return url;
      }
      return null;
    },
    []
  );

  useEffect(() => {
    let isMounted = true;

    const hasPhotoChanged =
      lastFetchedPhotoRef.current.id !== petId ||
      lastFetchedPhotoRef.current.photoKey !== photoKey ||
      lastFetchedPhotoRef.current.imageUrl !== imageUrl;

    const currentCacheKey = photoKey || imageUrl || petId;
    const cachedUrl = globalPhotoCache[currentCacheKey];

    if (hasPhotoChanged) {
      // Update the ref immediately
      lastFetchedPhotoRef.current = { id: petId, photoKey, imageUrl };

      if (cachedUrl) {
        // Defer setState to avoid update during commit (max update depth in commitDeletionEffects)
        const url = cachedUrl;
        setTimeout(() => setSignedImageUrl(url), 0);
        isFetchingRef.current = false;
      } else {
        setTimeout(() => setSignedImageUrl(null), 0);
        isFetchingRef.current = true;
        fetchSignedPhoto(petId, photoKey, imageUrl).then((url) => {
          isFetchingRef.current = false;
          if (!isMounted) return;
          if (url) setSignedImageUrl(url);
        });
      }
    } else if (
      !signedImageUrl &&
      (photoKey || imageUrl) &&
      !isFetchingRef.current
    ) {
      if (cachedUrl) {
        const url = cachedUrl;
        setTimeout(() => setSignedImageUrl(url), 0);
      } else {
        // Update the ref immediately
        lastFetchedPhotoRef.current = { id: petId, photoKey, imageUrl };

        isFetchingRef.current = true;
        fetchSignedPhoto(petId, photoKey, imageUrl).then((url) => {
          isFetchingRef.current = false;
          if (!isMounted) return;
          if (url) setSignedImageUrl(url);
        });
      }
    }

    return () => {
      isMounted = false;
    };
  }, [petId, photoKey, imageUrl, fetchSignedPhoto]); // Do not include signedImageUrl — effect only reacts to identity/photo inputs and sets URL; including it can cause max update depth

  return {
    signedImageUrl,
    setSignedImageUrl,
    fetchSignedPhoto,
    lastFetchedPhotoRef,
  };
}
